import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Spinner,
    Alert,
    Button,
    Card,
    Badge,
    Row,
    Col,
    Collapse,
    Dropdown,
    Form,
    ListGroup,
} from 'react-bootstrap';

import { useParams, useNavigate, Link } from 'react-router-dom';
import applicationService from '@/shared/services/applicationService';
import PageHeader from '@/components/common/PageHeader';
import ActionBar from '@/components/common/ActionBar';
import budgetService from '@/shared/services/budgetService';
import {
    canResubmit as canResubmitRequest,
    programMessagesOf,
    requestOutcomeOf,
    returnedNoteFor,
} from '@/shared/utils/requestOutcome';
import WizardStepIndicator from '@/components/common/WizardStepIndicator';
import useStepChangeFocus from '@/shared/hooks/useStepChangeFocus';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import useConsumeLocationState from '@/shared/hooks/useConsumeLocationState';
import useSavedProgress from '@/shared/hooks/useSavedProgress';
import useLeaveGuard from '@/shared/hooks/useLeaveGuard';
import SaveProgressStatus from '@/components/common/SaveProgressStatus';
import DecisionHistory from '@/components/common/DecisionHistory';
import DeleteDraftButton from './DeleteDraftButton';
import WithdrawApplicationButton from './WithdrawApplicationButton';
import NomineesPanel from './NomineesPanel';
import StartRevisionButton from './StartRevisionButton';
import UpdateMeetingsButton, { toldOf } from './UpdateMeetingsButton';
import CoordinatorEditRequest, {
    coordinatorEditablePrograms,
    editingOffForRevision,
    laAwardOf,
} from './CoordinatorEditRequest';
import RequestHistory from './RequestHistory';
import InstructorPanel from './InstructorPanel';
import ConfirmDecisionModal from './ConfirmDecisionModal';
import RevisionNote from './RevisionNote';
import NomineeOfferPicker, {
    defaultOffered,
    nomineesOf,
    requestedLACount,
} from './NomineeOfferPicker';
import { resumeDraftPath } from './draftApplication';
import {
    ALREADY_SUBMITTED_LEFT_MESSAGE,
    ALREADY_SUBMITTED_SAVE_NOTE,
    saveAndSubmit,
} from './submitOutcome';
import programService from '@/shared/services/programService';
import { subjectUnavailableReasons } from '@/shared/utils/programEligibility';
import termService from '@/shared/services/termService';
import { decisionService } from '@/shared/services/decisionService';
import { nominationService } from '@/shared/services/nominationService';
import {
    awardedCountText,
    decisionOutcome,
} from '@/shared/utils/applicationStatus';
import {
    denverDayOf,
    formatAnswerDay,
    formatReleaseMoment,
    releaseMoment,
} from '@/shared/utils/deadline';
import StaffingPanel from './StaffingPanel';
import { useTerm } from '@/shared/contexts/TermContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { getCoordinatorProgram } from '@/shared/utils/coordinatorRole';
import { EMAIL_PATTERN } from '@/shared/utils/email';
import './RankedPreferences.css';
import CreateApplicationSection6, {
    getGeneralAcknowledgmentErrors,
} from './CreateApplicationSection6';
import CreateApplicationSection7, {
    getReviewConfirmationErrors,
} from './CreateApplicationSection7';
import ApplicationSummary, {
    getConfirmedAnswers,
    getRelevantNamedStudents,
    getSavedNamedStudents,
    toProgramPreferences,
} from './ApplicationSummary';
import CreateApplicationSection4, {
    getDefaultCourseContext,
    getCourseContextErrors,
} from './CreateApplicationSection4';
import CreateApplicationSection5, {
    getDefaultProgramResponses,
    getProgramQuestionnaireErrors,
    programResponsesFromSaved,
} from './CreateApplicationSection5';
import { getProgramSelectionErrors } from './CreateApplicationSection2';
import {
    getAcknowledgementContractErrors,
    LACommitmentCards,
    useLARequirements,
} from './CreateApplicationSection3';
import {
    getCourseConfirmationOtherFieldErrors,
    CRN_PATTERN,
    MeetingFields,
    OtherTextField,
} from './CreateApplicationSection1';
import { meetingsFromSaved } from './meetings';
import { inProgramDisplayOrder } from '@/shared/utils/programOrder';
import { isSwitchedOff } from '@/shared/utils/programInfo';
import ProgramComingSoon from '@/components/common/ProgramComingSoon';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { requestInstructorName } from '@/shared/utils/requestInstructor';
import { hasStandingAward } from '@/shared/utils/applicationStatus';
import './ApplicationDetailPage.css';

const PROGRAMS = inProgramDisplayOrder([
    {
        key: 'si',
        label: 'SI',
        full: 'Supplemental Instruction',
        bg: 'success',
        description:
            'Peer-led study sessions for historically difficult courses',
    },
    {
        key: 'ta',
        label: 'TA',
        full: 'Teaching Assistant',
        bg: 'primary',
        description:
            'Structured assistance for high-enrollment or high-DFW courses',
    },
    {
        key: 'la',
        label: 'LA',
        full: 'Learning Assistant',
        bg: '',
        badgeClass: 'badge-la',
        description: 'Evidence-based teaching support in STEM courses',
    },
]);

// The server also refuses decided, approved, rejected and waitlisted
const WITHDRAWABLE_STATUSES = [
    'submitted',
    'under_review',
    'revision_requested',
];

// Where the decision card can show: a new decision while the application
// is open ('decided' means another program decided first), or a waitlisted
// one that can still be revisited
const NEW_DECISION_STATUSES = ['submitted', 'under_review', 'decided'];
// Also statuses where a program can already have a decision
const WAITLIST_DECISION_STATUSES = ['decided', 'waitlisted', 'approved'];
// The only statuses that need the program's own decision looked up (any
// decision moves a submitted one on): a held one can be changed (#165)
const OWN_DECISION_STATUSES = [...WAITLIST_DECISION_STATUSES, 'rejected'];

// A standing award, as routingService.isStandingAward has it
const isStandingAward = (decision) =>
    decision.finalStatus !== 'voided' &&
    (['approved', 'partial'].includes(decision.type) ||
        (decision.type === 'contingent' &&
            decision.finalStatus === 'finalized'));

// The program's own decision in a few words, e.g. "Approved, 2 of 3 LAs awarded"
function ownDecisionLabel(decision) {
    const label = decisionOutcome(decision).label;
    const count = awardedCountText(decision);
    return count ? `${label}, ${count}` : label;
}

const PROGRAM_CONFIG = {
    si: { bg: 'success', label: 'SI', full: 'Supplemental Instruction' },
    ta: { bg: 'primary', label: 'TA', full: 'Teaching Assistant' },
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        badgeClass: 'badge-la',
    },
};

// The note after a coordinator's save. A change to a held LA award isn't
// emailed: the faculty member sees the award when LA releases its decisions.
function coordinatorEditedNote(
    { changed, emailed, emailedFields },
    facultyEmailed
) {
    if (changed.length === 0)
        return 'Nothing was changed, so no email was sent.';
    const saved = `Changes saved: ${changed.join(', ')}.`;
    const heldAward =
        'The faculty member sees the new award when LA releases its decisions.';
    if (emailed === false) return `${saved} ${heldAward}`;
    const leftOut =
        emailedFields && changed.some((f) => !emailedFields.includes(f));
    return leftOut
        ? `${saved} ${facultyEmailed} emailed a summary of the other changes. ${heldAward}`
        : `${saved} ${facultyEmailed} emailed a summary of what changed.`;
}

// Same rule as refuseIfLAAwardNeedsInstructor in the server's
// decision.controller.js (#455) — keep the two in sync. LA-ranked only,
// since only LA refuses the award.
function isInstructorMissing(app) {
    const cc = app?.courseConfirmation || {};
    return (
        !!app?.isSubmittedOnBehalf &&
        !app.instructorOfRecord &&
        !!app.programPreferences?.some((p) => p.programType === 'la') &&
        (!cc.instructorOfRecordName?.trim() ||
            !cc.instructorOfRecordEmail?.trim())
    );
}

function ApplicationDetailPage() {
    const { currentTerm } = useTerm();
    const { id } = useParams();
    const navigate = useNavigate();
    // Reopen on the Applications page lands here ready to edit
    const arrival = useConsumeLocationState(['startRevision', 'reopened']);
    const openEditorOnLoadRef = useRef(!!arrival.startRevision);
    const { userProfile, effectiveRoles } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    // Shared fixed si/ta/la scan order (client/src/shared/utils/
    // coordinatorRole.js), matching the server and CoursesPage.js — a
    // user holding more than one coordinator role at once must resolve
    // to the same program everywhere, not just here.
    const coordinatorProgram = getCoordinatorProgram(roles) ?? null;
    const isCoordinator =
        coordinatorProgram !== null || roles.includes('admin');
    // The load effect runs on the id alone; it reads the viewer from here
    const mayOpenEditorRef = useRef(null);
    mayOpenEditorRef.current = (data) =>
        !isCoordinator && canResubmitRequest(data, userProfile);
    // Coordinators reach this page outside the program dashboards too, so the
    // review panel and the links back into a dashboard are gated here as
    // well: otherwise coordinatorReview would still render through this
    // shared page with the flag off. It only takes the panel away; who counts
    // as a coordinator for this application is unchanged.
    const coordinatorReviewEnabled = useFeature('coordinatorReview');
    const reviewProgram = coordinatorReviewEnabled ? coordinatorProgram : null;
    const hiringEnabled = useFeature('hiring');

    // Decision panel state
    const [decisionNotes, setDecisionNotes] = useState('');
    const [decisionError, setDecisionError] = useState(null);
    const [finishLaterNote, setFinishLaterNote] = useState(null);
    // The outcome waiting on the confirm dialog, or null when it's closed
    const [confirmingDecision, setConfirmingDecision] = useState(null);
    // Why the server refused a confirmed decision, shown once the dialog
    // closes. Outside the card, which the reload can take away
    const [refusedDecision, setRefusedDecision] = useState(null);
    const refusedDecisionRef = useRevealAlert(refusedDecision);
    // This program's own decision: undefined while loading, null if none.
    // A waitlisted one can still be approved or declined (#470)
    const [ownDecision, setOwnDecision] = useState(undefined);
    const [ownDecisionLoadFailed, setOwnDecisionLoadFailed] = useState(false);
    // Bumped to look the decision up again after a refused submit
    const [ownDecisionReload, setOwnDecisionReload] = useState(0);
    const ownDecisionWaitlisted = ownDecision?.type === 'waitlisted';
    // Whether faculty can see it yet; unknown counts as not yet
    const [ownDecisionReleased, setOwnDecisionReleased] = useState(false);
    // Whether the decision card is open to change the held decision
    const [changingDecision, setChangingDecision] = useState(false);
    const changeCardRef = useRevealAlert(changingDecision);

    // Whether the request's answers are unfolded; null until toggled, so each
    // viewer starts from their own default
    const [requestOpen, setRequestOpen] = useState(null);
    // The program's budget for the term, for the decision panel: undefined
    // while loading, else { state: 'ready' | 'none' | 'error', ... }
    const [budget, setBudget] = useState(undefined);

    // View state
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eligiblePrograms, setEligiblePrograms] = useState(null);
    const [unavailableReasons, setUnavailableReasons] = useState({});
    // The term this application was actually filed under — not
    // necessarily the globally-active `currentTerm` from useTerm(), which
    // may have rolled forward by the time a revision-requested application
    // is reopened for editing.
    const [applicationTerm, setApplicationTerm] = useState(null);

    // The fields a coordinator's edit just changed, for the note it leaves;
    // null when there is no note to show
    const [coordinatorEdited, setCoordinatorEdited] = useState(null);
    // The note after the submitter updates the meetings
    const [meetingsNote, setMeetingsNote] = useState(null);
    // The dialog closes without returning focus, so the note takes it
    const meetingsNoteRef = useRevealAlert(meetingsNote);
    const coordinatorEditedRef = useRevealAlert(coordinatorEdited);

    // What a reopen did with the CRN, shown above the editor
    const [reopenOutcome, setReopenOutcome] = useState(
        arrival.reopened || null
    );

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [submitError, setSubmitError] = useState(null);
    // Submitted from somewhere else, so this page can't save any more: the
    // save line and the leave prompt would only repeat the refused save
    // under the submit alert (#394)
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const submitErrorRef = useRevealAlert(submitError);
    // Whether the user has already tried (and failed) to advance past the
    // current step — same pattern as CreateApplication.js's
    // validationAttempted, so the create and edit/resubmit wizards report a
    // blocked advance the same way (ticket #384).
    const [validationAttempted, setValidationAttempted] = useState(false);
    useEffect(() => {
        setValidationAttempted(false);
    }, [step]);

    // Eligibility & Course on a returned request: the section's CRN, number,
    // enrollment and meeting times change here, plus the blank "Other"
    // descriptions and the instructor (#403/#384, #455). The course and the
    // modality stay as filed.
    const [courseConfirmation, setCourseConfirmation] = useState({
        crn: '',
        enrollment: '',
        sectionNumber: '',
        modality: '',
        otherModality: '',
        instructorOfRecordName: '',
        instructorOfRecordEmail: '',
        meetings: [],
    });
    // Which of the CRN, enrollment and section number were typed in, so only
    // those are sent. Kept once set, because each step change saves: changing
    // a CRN back has to send the old value over the one already saved.
    const [sectionEdited, setSectionEdited] = useState({});
    // Whether a meeting changed, so an untouched list isn't sent back over
    // what's on file
    const [meetingsEdited, setMeetingsEdited] = useState(false);
    // On-behalf application with no instructor yet (#455) — LA can't award
    // it until one is named, so the step offers the fields.
    const [instructorNeedsFix, setInstructorNeedsFix] = useState(false);
    // CRN or an "Other" description is blank on file, which the server
    // refuses at resubmit, so the alert only claims that when it's true.
    const [requiredFieldsNeedFix, setRequiredFieldsNeedFix] = useState(false);
    // The live CRN check's answer for the CRN it was asked about. A valid CRN
    // with no answer yet is still being checked.
    const [crnCheck, setCrnCheck] = useState(null);

    // Step 1: Program Preferences
    const [selectedPrograms, setSelectedPrograms] = useState([]);

    // Step 2: Course Context
    const [courseContext, setCourseContext] = useState(getDefaultCourseContext);

    // Step 3: Named Students
    const [namedStudents, setNamedStudents] = useState([]);

    // The nominees the coordinator has chosen to offer, for one application.
    // Null until they change it, so the choice starts from the default
    const [offerChoice, setOfferChoice] = useState(null);
    const [nomineeAnswerBy, setNomineeAnswerBy] = useState(null);
    // The LA count the coordinator awards, for the application it was chosen on
    const [awardChoice, setAwardChoice] = useState(null);

    // Step 4: Program-Specific Responses
    const [programResponses, setProgramResponses] = useState(
        getDefaultProgramResponses
    );

    // Step 4: General Acknowledgments
    const [acknowledgesGeneralTerms, setAcknowledgesGeneralTerms] =
        useState(false);

    // Step 5: Review and Submit
    const [confirmsSubmissionAccuracy, setConfirmsSubmissionAccuracy] =
        useState(false);
    // Distinguishes "never confirmed yet" from "was confirmed, then
    // invalidated by an edit" — only the latter gets an explanatory note
    // on Section 7 (see confirmationWasInvalidated below), so loading an
    // application that was never confirmed doesn't show a "your
    // confirmation was cleared" message that was never true. Mirrors
    // confirmsSubmissionAccuracy in a ref so the invalidation effect below
    // can read the latest value without adding it as its own dependency —
    // that would make checking the box re-trigger the effect and
    // immediately uncheck itself.
    const [confirmationWasInvalidated, setConfirmationWasInvalidated] =
        useState(false);
    const confirmsSubmissionAccuracyRef = useRef(false);

    // seedFormFromApplication below sets every answer the invalidation
    // effect watches, in one batch, which would otherwise trip that effect
    // and wipe out the pre-fill it just set. Set to true right before a
    // seed so that effect's next run is skipped, not treated as an edit.
    const skipNextConfirmationInvalidateRef = useRef(true);

    const handleConfirmsSubmissionAccuracyChange = (checked) => {
        setConfirmsSubmissionAccuracy(checked);
        confirmsSubmissionAccuracyRef.current = checked;
        if (checked) setConfirmationWasInvalidated(false);
    };

    useEffect(() => {
        const loadApplication = async () => {
            try {
                setEditMode(false);
                setStep(1);
                setSubmitError(null);
                // Don't carry the decision panel over from another application
                setDecisionNotes('');
                setDecisionError(null);
                setConfirmingDecision(null);
                setRefusedDecision(null);
                setOwnDecision(undefined);
                setOwnDecisionLoadFailed(false);
                setChangingDecision(false);
                setCoordinatorEdited(null);
                setRequestOpen(null);
                setLoading(true);
                const data = await applicationService.getApplicationById(id);
                setApplication(data);
                // Same rule as the Edit and resubmit button: only the filer
                if (
                    openEditorOnLoadRef.current &&
                    mayOpenEditorRef.current(data)
                ) {
                    setEditMode(true);
                }
                openEditorOnLoadRef.current = false;

                try {
                    const result = await programService.getEligiblePrograms(
                        data.course._id
                    );
                    setEligiblePrograms(
                        result.eligiblePrograms.map((p) => p.programType)
                    );
                    setUnavailableReasons(subjectUnavailableReasons(result));
                } catch {
                    setEligiblePrograms(null);
                    setUnavailableReasons({});
                }

                try {
                    setApplicationTerm(
                        await termService.getTermByCode(data.term)
                    );
                } catch {
                    setApplicationTerm(null);
                }
            } catch {
                setError('Failed to load application. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadApplication();
    }, [id]);

    // TA never gets the decision card, and the server refuses a program the
    // application doesn't rank
    const ranksReviewProgram =
        !!reviewProgram &&
        reviewProgram !== 'ta' &&
        !!application?.programPreferences?.some(
            (p) => p.programType === reviewProgram
        );
    // Only statuses where the card can show need the lookup
    const needsOwnDecision =
        ranksReviewProgram &&
        OWN_DECISION_STATUSES.includes(application?.status);

    useEffect(() => {
        if (!needsOwnDecision) return undefined;
        let cancelled = false;
        const loadOwnDecision = async () => {
            let mine;
            let released = false;
            try {
                // Program-scoped, so other programs' notes never reach us
                const status = await decisionService.getRoutingStatus(
                    id,
                    reviewProgram
                );
                mine = status?.alreadyDecided ? status.decision : null;
                released = status?.decisionReleased === true;
            } catch {
                // Without it we can't tell a new decision from an existing
                // one, so the card stays hidden
                if (!cancelled) {
                    setOwnDecision(undefined);
                    setOwnDecisionLoadFailed(true);
                }
                return;
            }
            if (cancelled) return;
            setOwnDecision(mine);
            setOwnDecisionReleased(released);
            setOwnDecisionLoadFailed(false);
        };
        loadOwnDecision();
        return () => {
            cancelled = true;
        };
    }, [id, reviewProgram, needsOwnDecision, ownDecisionReload]);

    // Read once per application, for the decision panel's budget line
    const budgetTerm = ranksReviewProgram ? application?.term : null;
    useEffect(() => {
        if (!budgetTerm) return undefined;
        let cancelled = false;
        setBudget(undefined);
        const loadBudget = async () => {
            let next;
            try {
                const data = await budgetService.getBudget(
                    reviewProgram,
                    budgetTerm
                );
                next = {
                    state: 'ready',
                    allocatedPositions: data?.budget?.allocatedPositions,
                    remainingPositions: data?.remainingPositions,
                };
            } catch (err) {
                next = {
                    state: err?.response?.status === 404 ? 'none' : 'error',
                };
            }
            if (!cancelled) setBudget(next);
        };
        loadBudget();
        return () => {
            cancelled = true;
        };
    }, [reviewProgram, budgetTerm]);

    const seedFormFromApplication = useCallback((app) => {
        const savedCourseConfirmation = app.courseConfirmation || {};
        const seededCourseConfirmation = {
            crn: savedCourseConfirmation.crn || '',
            enrollment: String(savedCourseConfirmation.enrollment ?? ''),
            sectionNumber: savedCourseConfirmation.sectionNumber || '',
            modality: savedCourseConfirmation.modality || '',
            otherModality: savedCourseConfirmation.otherModality || '',
            instructorOfRecordName:
                savedCourseConfirmation.instructorOfRecordName || '',
            instructorOfRecordEmail:
                savedCourseConfirmation.instructorOfRecordEmail || '',
            meetings: meetingsFromSaved(savedCourseConfirmation),
        };
        setCourseConfirmation(seededCourseConfirmation);
        setMeetingsEdited(false);
        setSectionEdited({});
        const missingInstructor = isInstructorMissing(app);
        const missingRequired =
            !CRN_PATTERN.test(seededCourseConfirmation.crn) ||
            getCourseConfirmationOtherFieldErrors(
                seededCourseConfirmation,
                'adp'
            ).length > 0;
        setInstructorNeedsFix(missingInstructor);
        setRequiredFieldsNeedFix(missingRequired);
        setCourseContext((prev) => ({
            ...prev,
            ...(app.courseContext || {}),
        }));
        setNamedStudents(app.namedStudents || []);
        setProgramResponses(
            programResponsesFromSaved(app.programSpecificResponses)
        );
        setSelectedPrograms(
            app.programPreferences?.map((pref) => ({
                ...PROGRAM_CONFIG[pref.programType],
                key: pref.programType,
                rank: pref.rank,
            })) || []
        );
        setAcknowledgesGeneralTerms(app.acknowledgesGeneralTerms === true);
        const seededConfirmsSubmissionAccuracy =
            app.confirmsSubmissionAccuracy === true;
        setConfirmsSubmissionAccuracy(seededConfirmsSubmissionAccuracy);
        confirmsSubmissionAccuracyRef.current =
            seededConfirmsSubmissionAccuracy;
        setConfirmationWasInvalidated(false);
        skipNextConfirmationInvalidateRef.current = true;
    }, []);

    // Editing an answer Review & Submit shows invalidates a confirmation
    // already given for the old values — a backstop alongside
    // requestRevision clearing it server-side. The course and instructor of
    // record can't be edited here, so they're left out; courseConfirmation
    // covers the Eligibility & Course step's fields.
    const confirmedAnswers = getConfirmedAnswers({
        courseConfirmation,
        selectedPrograms,
        courseContext,
        namedStudents,
        programResponses,
    });
    useEffect(() => {
        if (skipNextConfirmationInvalidateRef.current) {
            skipNextConfirmationInvalidateRef.current = false;
            return;
        }
        if (confirmsSubmissionAccuracyRef.current) {
            setConfirmationWasInvalidated(true);
            confirmsSubmissionAccuracyRef.current = false;
        }
        setConfirmsSubmissionAccuracy(false);
        // A fixed-length list from getConfirmedAnswers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, confirmedAnswers);

    useEffect(() => {
        if (!application) return;
        seedFormFromApplication(application);
    }, [application, seedFormFromApplication]);

    // A changed CRN is checked against the other requests this term as it is
    // typed, so a collision shows on the field rather than as a refused save.
    // A failed check counts as no conflict: the unique index still refuses a
    // real duplicate at save and at submit.
    const applicationId = application?._id;
    const applicationCode = application?.term;
    const savedCrnOnFile = application?.courseConfirmation?.crn;
    useEffect(() => {
        const crn = courseConfirmation.crn;
        if (!CRN_PATTERN.test(crn) || crn === savedCrnOnFile) return undefined;
        let stale = false;
        const timer = setTimeout(async () => {
            let result = null;
            try {
                result = await applicationService.checkCrn(
                    applicationCode,
                    crn,
                    applicationId
                );
            } catch {
                result = null;
            }
            if (!stale) setCrnCheck({ crn, exists: !!result?.exists });
        }, 300);
        return () => {
            stale = true;
            clearTimeout(timer);
        };
    }, [
        courseConfirmation.crn,
        savedCrnOnFile,
        applicationCode,
        applicationId,
    ]);

    // Decisions are tied to the ranking, so a returned application a program
    // already decided on keeps it (#470)
    const rankingLocked =
        application?.status === 'revision_requested' &&
        !!application?.hasDecisions;

    // The server refuses a CRN change once a program has decided (#470)
    const crnLocked = rankingLocked && !!savedCrnOnFile;

    // A request on one section's catalog record keeps the section number and
    // CRN it was filed with: the server ignores both on this save
    const recordOwnsCrn = !!application?.course?.crn;
    const recordOwnsSection = !!application?.course?.section;

    const toggleProgram = useCallback((program) => {
        setSelectedPrograms((prev) => {
            const exists = prev.find((p) => p.key === program.key);
            if (exists) {
                // Remove and re-rank
                const filtered = prev.filter((p) => p.key !== program.key);
                return filtered.map((p, i) => ({ ...p, rank: i + 1 }));
            }
            return [...prev, { ...program, rank: prev.length + 1 }];
        });
    }, []);

    const moveProgram = useCallback((index, direction) => {
        setSelectedPrograms((prev) => {
            const arr = [...prev];
            const swapIndex = index + direction;
            if (swapIndex < 0 || swapIndex >= arr.length) return prev;
            [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
            return arr.map((p, i) => ({ ...p, rank: i + 1 }));
        });
    }, []);

    // Moving a program to either end disables the arrow just pressed, which
    // drops keyboard focus to the page, so focus moves to its other arrow
    // (same as CreateApplicationSection2)
    const refocusRankId = useRef(null);
    useEffect(() => {
        if (!refocusRankId.current) return;
        document.getElementById(refocusRankId.current)?.focus();
        refocusRankId.current = null;
    }, [selectedPrograms]);
    const moveRanked = (index, direction, key) => {
        moveProgram(index, direction);
        const target = index + direction;
        if (target === 0) refocusRankId.current = `adp-rank-${key}-down`;
        if (target === selectedPrograms.length - 1) {
            refocusRankId.current = `adp-rank-${key}-up`;
        }
    };

    const addNamedStudent = useCallback((forProgram = 'ta') => {
        setNamedStudents((prev) => [
            ...prev,
            {
                name: '',
                email: '',
                studentId: '',
                forProgram,
            },
        ]);
    }, []);

    const updateNamedStudent = useCallback((index, field, value) => {
        setNamedStudents((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    const removeNamedStudent = useCallback((index) => {
        setNamedStudents((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const updateProgramResponse = useCallback((programKey, field, value) => {
        setProgramResponses((prev) => ({
            ...prev,
            [programKey]: { ...prev[programKey], [field]: value },
        }));
    }, []);

    // The only course confirmation fields this flow can change (see
    // renderCourseConfirmation). A blank "Other" description or CRN on file
    // (requiredFieldsNeedFix) sends the CRN and description whether or not
    // they were touched, as the step always has; otherwise each is sent once
    // it is edited.
    const courseConfirmationFixup = {
        ...(!recordOwnsCrn &&
            (sectionEdited.crn || requiredFieldsNeedFix) && {
                crn: courseConfirmation.crn,
            }),
        // A blank enrollment is not sent: the server keeps what is on file
        ...(sectionEdited.enrollment &&
            courseConfirmation.enrollment.trim() !== '' && {
                enrollment: Number(courseConfirmation.enrollment),
            }),
        ...(!recordOwnsSection &&
            sectionEdited.sectionNumber && {
                sectionNumber: courseConfirmation.sectionNumber.trim(),
            }),
        ...((sectionEdited.otherModality || requiredFieldsNeedFix) && {
            otherModality: courseConfirmation.otherModality,
        }),
        // Whole, since the server replaces the list
        ...(meetingsEdited && { meetings: courseConfirmation.meetings }),
        // Only when the step offered them (#455). Kept on this object
        // rather than at the call site so Review & Submit, which spreads the
        // same object, previews exactly what gets sent.
        ...(instructorNeedsFix && {
            instructorOfRecordName:
                courseConfirmation.instructorOfRecordName.trim(),
            instructorOfRecordEmail:
                courseConfirmation.instructorOfRecordEmail.trim(),
        }),
    };

    // What gets submitted. Built once so Review & Submit shows exactly this.
    const specificResponses = {};
    selectedPrograms.forEach((p) => {
        if (programResponses[p.key]) {
            specificResponses[p.key] = programResponses[p.key];
        }
    });
    const applicationData = {
        programPreferences: toProgramPreferences(selectedPrograms),
        courseContext,
        programSpecificResponses: specificResponses,
        acknowledgesGeneralTerms,
        confirmsSubmissionAccuracy,
        // Rows filled in part way are kept while editing; the resubmit in
        // handleSubmit drops blank-named ones
        namedStudents: getSavedNamedStudents(namedStudents, selectedPrograms),
        // Only the fields the step edits: the server writes '' as a value,
        // so sending the read-only modality back could fail the enum on save
        ...(Object.keys(courseConfirmationFixup).length > 0 && {
            courseConfirmation: courseConfirmationFixup,
        }),
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        setSubmitting(true);
        try {
            // Through the save queue, so a step change's save still in
            // flight can't land after the submit
            const result = await saveAndSubmit({
                save: (payload) => save({ force: true, payload }),
                payload: {
                    ...applicationData,
                    namedStudents: getRelevantNamedStudents(
                        namedStudents,
                        selectedPrograms
                    ),
                },
                getId: () => id,
            });
            if (result.submitted) {
                markClean();
                navigate('/applications', {
                    state: {
                        success: 'Support request submitted successfully!',
                    },
                });
                return;
            }
            setSubmitError(result.message);
            if (result.alreadySubmitted) setAlreadySubmitted(true);
        } finally {
            setSubmitting(false);
        }
    };
    const hasTA = selectedPrograms.some((p) => p.key === 'ta');
    const hasLA = selectedPrograms.some((p) => p.key === 'la');
    // Only needed while editing an LA application
    const laRequirements = useLARequirements(
        application?.term,
        editMode && hasLA
    );

    // Also requires both LA acknowledgments, same as the create wizard's
    // separate Acknowledgments & Contract step (the cards are rendered inline within
    // this page's Program Questionnaire step instead). Collects every
    // applicable error instead of stopping at the first, same reasoning as
    // getProgramQuestionnaireErrors.
    const getProgramSpecificErrors = () => {
        const errors = getProgramQuestionnaireErrors(
            namedStudents,
            programResponses,
            selectedPrograms
        );
        if (hasLA) {
            errors.push(
                ...getAcknowledgementContractErrors(
                    programResponses.la,
                    'adp',
                    !!laRequirements.requirements,
                    laRequirements.noTerm
                )
            );
        }
        return errors;
    };

    const renderProgramSelection = () => (
        <div>
            <h2 className="h5 mb-3">Select Programs</h2>
            <p className="text-muted mb-3">
                Choose which peer support programs you&apos;d like for this
                course, then rank them by preference. Programs review
                applications independently based on their own timelines.
            </p>
            {rankingLocked && (
                <Alert variant="info">
                    A program has already decided on this application, so the
                    programs and their ranking can&apos;t change.
                </Alert>
            )}
            <div
                id="adp-program-selection-group"
                role="group"
                aria-label="Select programs"
                aria-invalid={programSelectionInvalid}
                aria-describedby={
                    programSelectionInvalid
                        ? 'adp-program-selection-group-error'
                        : undefined
                }
                tabIndex={-1}
                className={
                    programSelectionInvalid
                        ? 'border border-danger rounded p-2'
                        : undefined
                }
            >
                <Row className="mb-4">
                    {PROGRAMS.map((program) => {
                        const isSelected = selectedPrograms.some(
                            (p) => p.key === program.key
                        );
                        // A program's switch is global, not per term, so the
                        // current term's verdict answers for this one too.
                        const switchedOff = isSwitchedOff(
                            currentTerm?.applicationWindows?.[program.key]
                        );
                        const eligible =
                            !switchedOff &&
                            (eligiblePrograms === null ||
                                eligiblePrograms.includes(program.key));
                        // A program ranked before it became unavailable can
                        // still be removed, or the application could never
                        // be resubmitted.
                        const togglable =
                            !rankingLocked && (eligible || isSelected);
                        const ineligibleId = `adp-program-ineligible-${program.key}`;
                        const comingSoonId = `adp-program-coming-soon-${program.key}`;
                        return (
                            <Col md={4} key={program.key} className="mb-3">
                                {/* CreateApplicationSection2.js uses
                                role="checkbox"/aria-checked instead — this
                                card has no nested link forcing a split. */}
                                <Card
                                    role="button"
                                    tabIndex={togglable ? 0 : -1}
                                    aria-pressed={isSelected}
                                    aria-disabled={!togglable}
                                    aria-describedby={
                                        switchedOff
                                            ? comingSoonId
                                            : eligible
                                              ? undefined
                                              : ineligibleId
                                    }
                                    className={`h-100 ${togglable ? 'cursor-pointer' : 'cursor-not-allowed'} ${isSelected ? 'border-primary border-2' : ''}`}
                                    onClick={() =>
                                        togglable && toggleProgram(program)
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            togglable &&
                                            (e.key === 'Enter' || e.key === ' ')
                                        ) {
                                            e.preventDefault();
                                            toggleProgram(program);
                                        }
                                    }}
                                >
                                    <Card.Body className="text-center">
                                        {/* Only the program's own copy dims;
                                            the reason stays at full contrast. */}
                                        <div
                                            className={
                                                eligible
                                                    ? undefined
                                                    : 'opacity-50'
                                            }
                                        >
                                            <Badge
                                                bg={program.bg || ''}
                                                text={badgeTextFor(
                                                    program.bg || ''
                                                )}
                                                className={`fs-6 px-3 py-2 mb-2 ${program.badgeClass || ''}`}
                                            >
                                                {program.label}
                                            </Badge>
                                            <p className="fw-semibold mb-1">
                                                {program.full}
                                            </p>
                                            <p className="text-muted small mb-0">
                                                {program.description}
                                            </p>
                                        </div>
                                        {!eligible && !switchedOff && (
                                            <small
                                                id={ineligibleId}
                                                className="text-danger d-block mt-1"
                                            >
                                                {unavailableReasons[
                                                    program.key
                                                ] ||
                                                    'Not eligible for this course'}
                                            </small>
                                        )}
                                        {isSelected && (
                                            <div className="mt-2">
                                                <Badge bg="primary" pill>
                                                    Selected
                                                </Badge>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                                {/* Outside the card: its link cannot sit
                                    inside a role="button". */}
                                {switchedOff && (
                                    <ProgramComingSoon
                                        programType={program.key}
                                        id={comingSoonId}
                                        className="mt-2"
                                    />
                                )}
                            </Col>
                        );
                    })}
                </Row>
                {programSelectionInvalid && (
                    <p
                        id="adp-program-selection-group-error"
                        className="text-danger small mb-0"
                        role="alert"
                    >
                        Select at least one program.
                    </p>
                )}
            </div>

            {selectedPrograms.length > 0 && (
                <Card className="bg-light border-0">
                    <Card.Body>
                        <h3 className="h6">Your Ranked Preferences</h3>
                        <p className="text-muted small">
                            1 is your first choice. Use the arrows to reorder.
                        </p>
                        <ListGroup variant="flush">
                            {selectedPrograms.map((program, index) => (
                                <ListGroup.Item
                                    key={program.key}
                                    className="d-flex align-items-center justify-content-between gap-2 bg-transparent"
                                >
                                    <div className="d-flex align-items-center gap-2 gap-sm-3">
                                        <Badge
                                            bg="secondary"
                                            pill
                                            className="fs-6"
                                        >
                                            {program.rank}
                                        </Badge>
                                        <Badge
                                            bg={program.bg || ''}
                                            text={badgeTextFor(
                                                program.bg || ''
                                            )}
                                            className={`program-rank-badge ${program.badgeClass || ''}`}
                                        >
                                            {program.label}
                                        </Badge>
                                        <span className="text-break">
                                            {program.full}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row flex-shrink-0 gap-2">
                                        <Button
                                            variant="outline-primary"
                                            id={`adp-rank-${program.key}-up`}
                                            aria-label={`Move ${program.full} up`}
                                            disabled={
                                                rankingLocked || index === 0
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveRanked(
                                                    index,
                                                    -1,
                                                    program.key
                                                );
                                            }}
                                            className="rank-move-btn"
                                        >
                                            &#9650;
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            id={`adp-rank-${program.key}-down`}
                                            aria-label={`Move ${program.full} down`}
                                            disabled={
                                                rankingLocked ||
                                                index ===
                                                    selectedPrograms.length - 1
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveRanked(
                                                    index,
                                                    1,
                                                    program.key
                                                );
                                            }}
                                            className="rank-move-btn"
                                        >
                                            &#9660;
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card.Body>
                </Card>
            )}

            {hasTA && (
                <Alert variant="info" className="mt-3">
                    <strong>TA Selected:</strong> You will need to provide at
                    least one named student (name, email) for the TA program.
                </Alert>
            )}
        </div>
    );

    const renderCourseContext = () => (
        <CreateApplicationSection4
            value={courseContext}
            onChange={(patch) =>
                setCourseContext((prev) => ({ ...prev, ...patch }))
            }
            errorIds={errorIdsFor(courseContextErrors)}
        />
    );

    // Named Students and Program Details used to be two separate steps
    // only when hasTA; both are now handled by the same
    // CreateApplicationSection5 the create wizard uses, so the create and
    // edit/resubmit flows can't silently diverge on what the LA/TA/SI
    // questionnaire looks like or requires. The Acknowledgments & Contract cards aren't
    // part of that shared component (they're the create wizard's separate
    // Acknowledgments & Contract step) — rendered here instead, right after the
    // questionnaire, same as this page's own prior layout.
    const renderProgramQuestionnaire = () => (
        <div>
            <CreateApplicationSection5
                namedStudents={namedStudents}
                addNamedStudent={addNamedStudent}
                updateNamedStudent={updateNamedStudent}
                removeNamedStudent={removeNamedStudent}
                selectedPrograms={selectedPrograms}
                programResponses={programResponses}
                onProgramResponseChange={updateProgramResponse}
                hasTA={hasTA}
                courseConfirmation={{
                    ...application.courseConfirmation,
                    ...courseConfirmationFixup,
                }}
                laTrainingSessions={
                    laRequirements.requirements?.trainingSessions
                }
                errorIds={errorIdsFor(programSpecificErrors)}
            />
            {hasLA && (
                <>
                    <hr />
                    <h2 className="h5 mb-3">Acknowledgments &amp; Contract</h2>
                    <LACommitmentCards
                        la={programResponses.la}
                        onChange={(field, checked) =>
                            updateProgramResponse('la', field, checked)
                        }
                        idPrefix="adp"
                        requirements={laRequirements}
                        errorIds={errorIdsFor(programSpecificErrors)}
                    />
                </>
            )}
        </div>
    );

    // A returned request's section can be corrected here (the CRN, section
    // number, enrollment and meeting times), but not the course or the
    // modality it was filed with. A description left blank under an "Other"
    // choice (#403) and an instructor still unnamed (#455) are also filled in
    // here.
    const renderCourseConfirmation = () => {
        const hasError = (fieldId) =>
            errorIdsFor(courseConfirmationErrors).has(fieldId);
        const setField = (patch) => {
            setCourseConfirmation((prev) => ({ ...prev, ...patch }));
            setSectionEdited((prev) => ({
                ...prev,
                ...Object.fromEntries(Object.keys(patch).map((k) => [k, true])),
            }));
        };
        return (
            <div>
                <h2 className="h5 mb-3">Eligibility &amp; Course</h2>
                {requiredFieldsNeedFix && (
                    <Alert variant="info">
                        This application is missing information needed before it
                        can be resubmitted — a description for a course detail
                        recorded as &quot;Other,&quot; a CRN, or both. Fill in
                        what&apos;s below to continue.
                    </Alert>
                )}
                {instructorNeedsFix &&
                    selectedPrograms.some((p) => p.key === 'la') && (
                        <Alert variant="info">
                            LA can&apos;t award support until an instructor of
                            record is named. Add them below if you know who it
                            is — you can still resubmit without it.
                        </Alert>
                    )}
                <Card className="border-0 bg-light mb-3">
                    <Card.Body>
                        <div className="fw-semibold">
                            {application.course?.subject}{' '}
                            {application.course?.courseNumber}{' '}
                            <span className="fw-normal text-muted">
                                {application.course?.title}
                            </span>
                        </div>
                        <p
                            id="adp-course-locked-note"
                            className="text-muted small mb-0 mt-1"
                        >
                            To request support for a different course, withdraw
                            this request and start a new one.
                        </p>
                    </Card.Body>
                </Card>
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="adp-enrollment">
                        Maximum course enrollment{' '}
                        <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        id="adp-enrollment"
                        type="number"
                        min="0"
                        placeholder="e.g., 35"
                        value={courseConfirmation.enrollment}
                        onChange={(e) =>
                            setField({ enrollment: e.target.value })
                        }
                        required
                        isInvalid={hasError('adp-enrollment')}
                        aria-invalid={hasError('adp-enrollment')}
                        aria-describedby={
                            hasError('adp-enrollment')
                                ? 'adp-enrollment-help adp-enrollment-error'
                                : 'adp-enrollment-help'
                        }
                    />
                    <Form.Text id="adp-enrollment-help" className="text-muted">
                        Enter the section&apos;s enrollment cap, not the number
                        of seats currently filled.
                    </Form.Text>
                    {hasError('adp-enrollment') && (
                        <Form.Control.Feedback
                            type="invalid"
                            id="adp-enrollment-error"
                            role="alert"
                        >
                            This field is required.
                        </Form.Control.Feedback>
                    )}
                </Form.Group>
                <Row
                    className={
                        recordOwnsCrn || recordOwnsSection ? 'mb-1' : 'mb-3'
                    }
                >
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="adp-crn">
                                CRN <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                id="adp-crn"
                                type="text"
                                inputMode="numeric"
                                placeholder="e.g., 12345"
                                value={courseConfirmation.crn}
                                onChange={(e) =>
                                    setField({ crn: e.target.value })
                                }
                                required
                                disabled={crnLocked && !recordOwnsCrn}
                                readOnly={recordOwnsCrn}
                                className={
                                    recordOwnsCrn ? 'bg-body-secondary' : ''
                                }
                                isInvalid={hasError('adp-crn')}
                                aria-invalid={hasError('adp-crn')}
                                aria-describedby={
                                    hasError('adp-crn')
                                        ? 'adp-crn-error'
                                        : recordOwnsCrn
                                          ? 'adp-section-hint'
                                          : crnLocked
                                            ? 'adp-crn-locked-note'
                                            : undefined
                                }
                            />
                            {crnLocked && !recordOwnsCrn && (
                                <Form.Text
                                    id="adp-crn-locked-note"
                                    className="text-muted"
                                >
                                    A program has already decided on this
                                    request, so the CRN can&apos;t change.
                                </Form.Text>
                            )}
                            {crnCheckPending && (
                                <Form.Text className="d-flex align-items-center gap-2">
                                    <Spinner animation="border" size="sm" />
                                    Checking CRN…
                                </Form.Text>
                            )}
                            {hasError('adp-crn') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="adp-crn-error"
                                    role="alert"
                                >
                                    {recordOwnsCrn
                                        ? 'This request has no CRN on file, and it cannot be added here. Withdraw this request and start a new one.'
                                        : !CRN_PATTERN.test(
                                                courseConfirmation.crn
                                            )
                                          ? 'Enter the 5-digit CRN.'
                                          : crnConflict
                                            ? 'This CRN already has a support request.'
                                            : 'Still checking this CRN — please wait.'}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label htmlFor="adp-section-number">
                                Section # <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                id="adp-section-number"
                                type="text"
                                placeholder="e.g., 001"
                                value={courseConfirmation.sectionNumber}
                                onChange={(e) =>
                                    setField({ sectionNumber: e.target.value })
                                }
                                readOnly={recordOwnsSection}
                                className={
                                    recordOwnsSection ? 'bg-body-secondary' : ''
                                }
                                required
                                isInvalid={hasError('adp-section-number')}
                                aria-invalid={hasError('adp-section-number')}
                                aria-describedby={
                                    hasError('adp-section-number')
                                        ? 'adp-section-number-error'
                                        : recordOwnsSection
                                          ? 'adp-section-hint'
                                          : undefined
                                }
                            />
                            {hasError('adp-section-number') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="adp-section-number-error"
                                    role="alert"
                                >
                                    This field is required.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                {(recordOwnsCrn || recordOwnsSection) && (
                    <Form.Text id="adp-section-hint" className="d-block mb-3">
                        Set by the section this request is for. To use another
                        section, withdraw this request and start a new one.
                    </Form.Text>
                )}
                {instructorNeedsFix && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="adp-ior-name">
                                Instructor Name (if known)
                            </Form.Label>
                            <Form.Control
                                id="adp-ior-name"
                                type="text"
                                placeholder="Instructor full name"
                                value={
                                    courseConfirmation.instructorOfRecordName
                                }
                                onChange={(e) =>
                                    setField({
                                        instructorOfRecordName: e.target.value,
                                    })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="adp-ior-email">
                                Instructor Email (if known)
                            </Form.Label>
                            <Form.Control
                                id="adp-ior-email"
                                type="email"
                                placeholder="instructor@msudenver.edu"
                                value={
                                    courseConfirmation.instructorOfRecordEmail
                                }
                                onChange={(e) =>
                                    setField({
                                        instructorOfRecordEmail: e.target.value,
                                    })
                                }
                                isInvalid={hasError('adp-ior-email')}
                                aria-invalid={hasError('adp-ior-email')}
                                aria-describedby={
                                    hasError('adp-ior-email')
                                        ? 'adp-ior-email-error'
                                        : undefined
                                }
                            />
                            {hasError('adp-ior-email') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="adp-ior-email-error"
                                    role="alert"
                                >
                                    Enter a valid email address.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </>
                )}
                {courseConfirmation.modality === 'other' && (
                    <Form.Group className="mb-3">
                        <OtherTextField
                            id="adp-other-modality"
                            label="Describe the other modality"
                            placeholder="e.g. flipped classroom"
                            value={courseConfirmation.otherModality}
                            onChange={(otherModality) =>
                                setField({ otherModality })
                            }
                            hasError={hasError}
                        />
                    </Form.Group>
                )}
                <MeetingFields
                    idPrefix="adp"
                    meetings={courseConfirmation.meetings}
                    onChange={(meetings) => {
                        setMeetingsEdited(true);
                        setField({ meetings });
                    }}
                    hasError={hasError}
                />
            </div>
        );
    };

    // /term/:code has no per-caller applicationWindows (extensions), so
    // borrow them from /term/current when it's the same term.
    const generalAcknowledgmentTerm =
        applicationTerm && currentTerm?.code === applicationTerm.code
            ? {
                  ...applicationTerm,
                  applicationWindows: currentTerm.applicationWindows,
              }
            : applicationTerm;

    const renderGeneralAcknowledgments = () => (
        <CreateApplicationSection6
            acknowledgesGeneralTerms={acknowledgesGeneralTerms}
            onChange={setAcknowledgesGeneralTerms}
            term={generalAcknowledgmentTerm}
            programs={selectedPrograms}
            idPrefix="adp"
            errorIds={errorIdsFor(generalAcknowledgmentErrors)}
        />
    );

    const renderReviewAndSubmit = () => (
        <CreateApplicationSection7
            application={{
                ...application,
                ...applicationData,
                // What's on file, plus anything the fixup step filled in
                courseConfirmation: {
                    ...application.courseConfirmation,
                    ...courseConfirmationFixup,
                    ...(courseConfirmationFixup.schedule && {
                        schedule: {
                            ...application.courseConfirmation.schedule,
                            ...courseConfirmationFixup.schedule,
                        },
                    }),
                },
            }}
            confirmsSubmissionAccuracy={confirmsSubmissionAccuracy}
            wasInvalidated={confirmationWasInvalidated}
            onChange={handleConfirmsSubmissionAccuracyChange}
            idPrefix="adp"
            errorIds={errorIdsFor(reviewConfirmationErrors)}
            instructorMissing={
                !!application.isSubmittedOnBehalf &&
                !application.instructorOfRecord &&
                (!courseConfirmation.instructorOfRecordName.trim() ||
                    !courseConfirmation.instructorOfRecordEmail.trim())
            }
        />
    );

    // One entry per wizard step, in display order — same array-driven
    // shape as CreateApplication.js's allSections. `errors` is precomputed
    // per render (same reasoning as CreateApplication.js's allSections) so
    // canProceed and the error summary can't drift apart.
    const crnValid = CRN_PATTERN.test(courseConfirmation.crn);
    const savedCrn = application?.courseConfirmation?.crn;
    // The CRN on file is this application's own, so only a changed one is
    // checked against the others
    const crnCheckPending =
        crnValid &&
        courseConfirmation.crn !== savedCrn &&
        crnCheck?.crn !== courseConfirmation.crn;
    const crnConflict =
        crnCheck?.crn === courseConfirmation.crn && !!crnCheck.exists;
    // A request filed before meetings had a type still has its one meeting
    // without one, so that meeting may stay untyped; every other meeting
    // needs what the create wizard asks for
    const firstMeetingUntyped =
        !application?.courseConfirmation?.meetings?.[0]?.type;
    const courseConfirmationErrors = [
        ...(courseConfirmation.enrollment.trim() === ''
            ? [{ id: 'adp-enrollment', label: 'Maximum course enrollment' }]
            : []),
        ...(!crnValid || crnCheckPending || crnConflict
            ? [{ id: 'adp-crn', label: 'CRN' }]
            : []),
        ...(!courseConfirmation.sectionNumber.trim()
            ? [{ id: 'adp-section-number', label: 'Section #' }]
            : []),
        ...(instructorNeedsFix &&
        courseConfirmation.instructorOfRecordEmail.trim() &&
        !EMAIL_PATTERN.test(courseConfirmation.instructorOfRecordEmail.trim())
            ? [{ id: 'adp-ior-email', label: 'Instructor Email' }]
            : []),
        ...getCourseConfirmationOtherFieldErrors(courseConfirmation, 'adp', {
            required: true,
            firstUntyped: firstMeetingUntyped,
        }),
    ];
    const programSelectionErrors = getProgramSelectionErrors(
        selectedPrograms,
        'adp'
    );
    const programSelectionInvalid =
        validationAttempted &&
        programSelectionErrors.some(
            (e) => e.id === 'adp-program-selection-group'
        );
    const courseContextErrors = getCourseContextErrors(courseContext);
    const programSpecificErrors = getProgramSpecificErrors();
    const generalAcknowledgmentErrors = getGeneralAcknowledgmentErrors(
        acknowledgesGeneralTerms,
        'adp'
    );
    const reviewConfirmationErrors = getReviewConfirmationErrors(
        confirmsSubmissionAccuracy,
        'adp'
    );
    // Field errors only render as errorIds once the user has actually tried
    // to advance past an invalid step — see validationAttempted above.
    const errorIdsFor = (errors) =>
        validationAttempted ? new Set(errors.map((e) => e.id)) : new Set();

    const sections = [
        {
            label: 'Eligibility & Course',
            errors: courseConfirmationErrors,
            render: renderCourseConfirmation,
        },
        {
            label: 'Select Programs',
            errors: programSelectionErrors,
            render: renderProgramSelection,
        },
        {
            label: 'Course Context',
            errors: courseContextErrors,
            render: renderCourseContext,
        },
        {
            label: 'Program Details',
            errors: programSpecificErrors,
            render: renderProgramQuestionnaire,
        },
        {
            label: 'General Acknowledgments',
            errors: generalAcknowledgmentErrors,
            render: renderGeneralAcknowledgments,
        },
        {
            label: 'Review & Submit',
            errors: reviewConfirmationErrors,
            render: renderReviewAndSubmit,
        },
    ];

    useEffect(() => {
        setStep((prev) => Math.min(prev, sections.length));
    }, [sections.length]);

    const currentSection = sections[step - 1];
    const currentErrors = currentSection?.errors ?? [];
    const canProceed = currentErrors.length === 0;

    const stepTopRef = useRef(null);
    const stepContentRef = useRef(null);
    useStepChangeFocus(step, stepTopRef, stepContentRef);

    // Edit and resubmit saves as it goes, like the create wizard (#519): each
    // step change and "Save and finish later" store the answers, and the
    // application stays returned for revision until it is resubmitted.
    const canSaveRevision =
        editMode && application?.status === 'revision_requested';
    // Whether this edit has saved anything, so Save and close shows what the
    // server now holds rather than the copy loaded before editing
    const savedDuringEditRef = useRef(false);
    const persistRevision = async (payload) => {
        await applicationService.updateApplication(id, payload);
        savedDuringEditRef.current = true;
    };
    const { save, markClean, hasUnsavedChanges, saveState } = useSavedProgress({
        payload: applicationData,
        persist: persistRevision,
        // Also stops the save on leaving, which would only be refused
        canSave: canSaveRevision && !alreadySubmitted,
    });
    // The answers as loaded are the baseline when editing starts
    useEffect(() => {
        if (editMode) markClean();
    }, [editMode, markClean]);
    const saveRevision = () => {
        if (canSaveRevision) save().catch(() => {});
    };
    useLeaveGuard(
        canSaveRevision &&
            !submitting &&
            !alreadySubmitted &&
            (hasUnsavedChanges || saveState.status === 'saving'),
        async (proceed) => {
            try {
                await save();
            } catch {
                if (
                    window.confirm(
                        'Your latest changes could not be saved. Leave anyway and lose them?'
                    )
                ) {
                    proceed();
                }
                return;
            }
            proceed();
        }
    );
    const saveAndFinishLater = async () => {
        // Nothing can be saved (and a save with no changes would resolve and
        // claim it was), so go where the alert points
        if (alreadySubmitted) {
            navigate('/applications', {
                state: { success: ALREADY_SUBMITTED_LEFT_MESSAGE },
            });
            return;
        }
        let result;
        try {
            result = await save();
        } catch {
            // The status line says it failed and offers to try again
            return;
        }
        if (result?.skipped) {
            setFinishLaterNote("Nothing was saved: you're previewing.");
            return;
        }
        navigate('/applications', {
            state: {
                success:
                    'Your changes are saved. The application stays returned for revision until you resubmit it.',
            },
        });
    };
    // Steps save as they go, so leaving the editor keeps the edits rather
    // than discarding them: the last changes are saved, after any step save
    // still in flight, and the page then shows what the server holds. A
    // failed save keeps the editor open with the reason on the status line.
    // Once it's already submitted there's nothing to save, so it just closes
    // and reloads to show what was submitted.
    const saveAndClose = async () => {
        if (!alreadySubmitted) {
            try {
                await save();
            } catch {
                return;
            }
        }
        setEditMode(false);
        setStep(1);
        // setStep(1) is a no-op (and won't re-run the [step] reset effect)
        // when already on step 1. Reset explicitly so a close-then-re-edit
        // cycle at step 1 doesn't carry a prior blocked attempt's marked
        // fields into the freshly reseeded form.
        setValidationAttempted(false);
        setSubmitError(null);
        if (!savedDuringEditRef.current && !alreadySubmitted) {
            seedFormFromApplication(application);
            return;
        }
        // Reloading reseeds the form from what was saved
        savedDuringEditRef.current = false;
        try {
            setApplication(await applicationService.getApplicationById(id));
        } catch {
            seedFormFromApplication(application);
        }
    };

    // Same pattern as CreateApplication.js's attemptAdvance — Next/Submit
    // stay clickable, and a blocked attempt marks the missing fields, moves
    // focus to the first one, and shows a summary instead of just refusing.
    const attemptAdvance = (advance) => {
        if (canProceed) {
            advance();
            return;
        }
        setValidationAttempted(true);
        const firstErrorId = currentErrors[0]?.id;
        if (firstErrorId) {
            document.getElementById(firstErrorId)?.focus();
        }
    };

    // The request is now in revision_requested on the server; load it whole
    // (the answer to the start carries no populated course) and open the
    // existing editor on it
    const openEditorAfterStart = async (result) => {
        setApplication(await applicationService.getApplicationById(id));
        setReopenOutcome(result?.reopened || null);
        setEditMode(true);
    };

    // After a refused withdraw or decision, show the status that beat it
    const reloadApplication = async () => {
        try {
            setApplication(await applicationService.getApplicationById(id));
        } catch {
            // Keep what's on screen
        }
    };

    // A coordinator's edit answers with the request as a coordinator sees
    // it; the page reads it again for the fields only GET /:id adds
    const handleCoordinatorEdit = async ({
        application: edited,
        changed,
        emailed,
        emailedFields,
    }) => {
        setCoordinatorEdited({ changed, emailed, emailedFields });
        if (changed.length > 0) {
            setApplication(edited);
            await reloadApplication();
        }
    };

    // The answer is the request as this faculty member may see it, without
    // the decisions and the people only GET /:id adds, so the page reads it
    // again rather than showing it
    const handleMeetingsUpdated = async ({ changed, timesChanged }) => {
        if (changed.length === 0) return;
        const told = toldOf(application);
        setMeetingsNote(
            `Saved: ${changed.join(' and ').toLowerCase()}. ${told} were told${
                timesChanged
                    ? ', and so were the students placed in this section'
                    : ''
            }.`
        );
        await reloadApplication();
    };

    // Only an LA coordinator's award offers nominees
    const offerNominees =
        coordinatorProgram === 'la' ? nomineesOf(application) : [];
    // Only LA asks for a count; the card offers 1 to the requested number
    const requestedAwardCount =
        coordinatorProgram === 'la' ? requestedLACount(application) : 1;
    const awardedCount =
        awardChoice?.applicationId === id
            ? Math.min(awardChoice.count, requestedAwardCount)
            : requestedAwardCount;
    const offerLimit = awardedCount;
    const offered = (
        offerChoice?.applicationId === id
            ? offerChoice.emails
            : defaultOffered(offerNominees, offerLimit)
    ).slice(0, offerLimit);
    const nomineeDay =
        nomineeAnswerBy?.applicationId === id ? nomineeAnswerBy.day : '';
    const offerField = (type) =>
        type === 'approved' && offerNominees.length > 0
            ? {
                  offeredEmails: offered,
                  ...(hiringEnabled && nomineeDay && offered.length > 0
                      ? { nomineeRespondByDay: nomineeDay }
                      : {}),
              }
            : {};
    // The full request is the server's default, so only a lower award is sent
    const awardField = (type) =>
        type === 'approved' && awardedCount < requestedAwardCount
            ? { awardedCount }
            : {};

    // The dialog sends the message box as it stands, or nothing at all
    const messageField = (extras) =>
        extras?.messageToFaculty !== undefined
            ? { messageToFaculty: extras.messageToFaculty }
            : {};

    // Opens the decision card on the program's held decision, set to what it
    // says now: its count, and for an LA award the nominees it offers and
    // the day they answer by
    const startChange = async () => {
        setDecisionError(null);
        setRefusedDecision(null);
        const awarding = ['approved', 'partial', 'contingent'].includes(
            ownDecision.type
        );
        if (awarding && Number.isInteger(ownDecision.awardedCount)) {
            setAwardChoice({
                applicationId: id,
                count: ownDecision.awardedCount,
            });
        }
        if (awarding && offerNominees.length > 0) {
            try {
                const { nominations } =
                    await nominationService.getForApplication(id);
                const offeredNow = (nominations || []).filter(
                    (n) => n.programType === 'la' && n.status === 'offered'
                );
                setOfferChoice({
                    applicationId: id,
                    emails: offeredNow.map((n) => n.email),
                });
                const day = denverDayOf(
                    offeredNow.find((n) => n.respondBy)?.respondBy
                );
                if (day) setNomineeAnswerBy({ applicationId: id, day });
            } catch {
                // The picker starts from its usual choice instead
            }
        }
        setChangingDecision(true);
    };

    // Called from the confirm dialog, which shows any refusal itself
    const recordDecision = async (type, extras) => {
        if (type === 'revision') {
            // Sent as shown in the dialog
            await applicationService.requestRevision(
                id,
                decisionNotes.trim(),
                ...(extras ? [extras] : [])
            );
            navigate(`/programs/${coordinatorProgram}`, {
                state: { success: 'Application returned for revision.' },
            });
            return;
        }
        if (type === 'withdraw') {
            await decisionService.withdrawDecision(ownDecision._id, extras);
            navigate(`/programs/${coordinatorProgram}`, {
                state: {
                    success:
                        'Decision withdrawn. The application is undecided again for your program.',
                },
            });
            return;
        }
        // A program only gets one decision, so a waitlisted or a held one is
        // updated
        if (ownDecisionWaitlisted || changingDecision) {
            // Keep the earlier notes unless new ones were typed
            await decisionService.updateDecision(ownDecision._id, {
                type,
                ...(decisionNotes.trim() && { notes: decisionNotes }),
                ...offerField(type),
                ...awardField(type),
                ...messageField(extras),
                ...(extras?.reason && { reason: extras.reason }),
            });
        } else {
            await decisionService.createDecision({
                applicationId: id,
                programType: coordinatorProgram,
                type,
                notes: decisionNotes,
                ...offerField(type),
                ...awardField(type),
                ...messageField(extras),
            });
        }
        navigate(`/programs/${coordinatorProgram}`, {
            state: {
                success: changingDecision
                    ? 'Decision changed. Faculty see it when decisions are released.'
                    : 'Decision set successfully!',
            },
        });
    };

    const confirmDecision = (type) => {
        setDecisionError(null);
        setRefusedDecision(null);
        setConfirmingDecision(type);
    };

    // Once the dialog has closed: another decision may have landed
    // meanwhile, so show where things stand now
    const handleDecisionRefused = (message) => {
        setRefusedDecision(message);
        reloadApplication();
        setOwnDecisionReload((n) => n + 1);
    };

    // The notes go to the applicant, so they're needed before confirming
    const handleRequestRevision = () => {
        if (!decisionNotes.trim()) {
            setDecisionError(
                'Please provide revision notes before returning this application.'
            );
            return;
        }
        confirmDecision('revision');
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading application</p>
            </Container>
        );
    }

    if (error || !application) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    {error || 'Application not found.'}
                </Alert>
            </Container>
        );
    }

    // A new decision while the application is open, or a waitlisted one
    // that can still be approved or declined. 'decided' means another
    // program decided first.
    // Not looked up while submitted or under review, where there is none
    const noOwnDecision = needsOwnDecision ? ownDecision === null : true;
    // Faculty haven't seen it, so the program can still change it (#165).
    // A held waitlist keeps its card open, as any waitlist does; another
    // held decision opens it with Change decision
    const ownDecisionHeld = !!ownDecision && !ownDecisionReleased;
    const editingHeld =
        ownDecisionHeld && (changingDecision || ownDecisionWaitlisted);
    let canDecide =
        noOwnDecision && NEW_DECISION_STATUSES.includes(application.status);
    if (ownDecisionWaitlisted) {
        canDecide = WAITLIST_DECISION_STATUSES.includes(application.status);
    } else if (ownDecisionHeld) {
        canDecide = changingDecision;
    }
    const releaseDate =
        applicationTerm?.programWindows?.[coordinatorProgram]
            ?.decisionsReleased ?? null;
    const releaseAt = releaseMoment(releaseDate);
    const programHolds = ownDecision
        ? !ownDecisionReleased
        : !!releaseAt && releaseAt > new Date();
    const heldNote = releaseDate
        ? `Faculty haven't seen this decision yet. It is released on ${formatReleaseMoment(releaseDate)}, and you can change or withdraw it until then.`
        : "Faculty haven't seen this decision yet. You can change or withdraw it until it is released.";
    // Once another program has awarded support, a waitlist (or a held
    // decision being changed) can only be declined, not turned into a
    // second award
    const waitlistOvertaken =
        (ownDecisionWaitlisted || changingDecision) &&
        application.status === 'approved' &&
        !isStandingAward(ownDecision);
    const ownDecisionDenied =
        changingDecision && ownDecision?.type === 'denied';
    // The server refuses an LA award with no instructor named (#455). It
    // still takes a waitlist, but the page holds Waitlist back so the
    // coordinator sends it for revision first (#470)
    const awardBlocked =
        !waitlistOvertaken &&
        coordinatorProgram === 'la' &&
        isInstructorMissing(application);
    // Same rule as the server's requestRevision: open, or later while this
    // program hasn't settled it and nobody has awarded support
    const canRequestRevision =
        ['submitted', 'under_review'].includes(application.status) ||
        (['decided', 'waitlisted'].includes(application.status) &&
            (noOwnDecision || ownDecisionWaitlisted));

    // A decline frees the CRN only once the application ends up rejected,
    // which is certain only when this program is the one ranked
    let declineCrnEffect = 'maybe';
    if (waitlistOvertaken) {
        declineCrnEffect = 'kept';
    } else if (application.programPreferences?.length === 1) {
        declineCrnEffect = 'frees';
    }
    const decisionProgram = PROGRAM_CONFIG[coordinatorProgram];
    // Programs ranked above this one, which can make an approval contingent
    const ownRank = application.programPreferences?.find(
        (p) => p.programType === coordinatorProgram
    )?.rank;
    const higherRankedLabels = (application.programPreferences || [])
        .filter((p) => p.rank < ownRank)
        .sort((a, b) => a.rank - b.rank)
        .map((p) => PROGRAM_CONFIG[p.programType]?.label ?? p.programType);

    // The decisions the dialog can switch between
    const outcomes = [
        ...(!awardBlocked && !waitlistOvertaken ? ['approved'] : []),
        ...(!ownDecisionWaitlisted && !awardBlocked && !waitlistOvertaken
            ? ['waitlisted']
            : []),
        ...(!ownDecisionDenied ? ['denied'] : []),
    ];

    // Only the applicant: the server refuses anyone else, the instructor of
    // record included
    const applicantId = application.applicant?._id ?? application.applicant;
    const courseLabel =
        `${application.course?.subject ?? ''} ${application.course?.courseNumber ?? ''}`.trim();
    const isApplicant = !!userProfile?._id && applicantId === userProfile._id;
    // Faculty on this request: the applicant or the instructor of record, and
    // able to open My LA positions (the /my-positions route's roles)
    const instructorId =
        application.instructorOfRecord?._id ?? application.instructorOfRecord;
    const isRequestFaculty =
        (roles.includes('faculty') || roles.includes('admin')) &&
        (isApplicant ||
            (!!userProfile?._id &&
                !!instructorId &&
                instructorId === userProfile._id));
    // Who a coordinator's edit emails: the applicant and the instructor of
    // record (recipients.js facultyRecipients)
    const facultyNames = [
        ...new Set(
            [
                application.applicant?.name,
                application.isSubmittedOnBehalf
                    ? requestInstructorName(application)
                    : null,
            ].filter(Boolean)
        ),
    ];
    const facultyEmailed =
        facultyNames.length > 0
            ? `${facultyNames.join(' and ')} ${facultyNames.length > 1 ? 'were' : 'was'}`
            : 'The faculty member was';
    // isCoordinator covers admin too
    const canResubmit =
        application.status === 'revision_requested' &&
        !isCoordinator &&
        isApplicant;
    // A returned request someone else filed, such as the instructor it names
    const returnedNote = isCoordinator
        ? null
        : returnedNoteFor(application, userProfile);
    const canWithdraw =
        WITHDRAWABLE_STATUSES.includes(application.status) &&
        // The server refuses it once a program has decided (#470)
        !application.hasDecisions &&
        isApplicant;
    // A decision a program has recorded but not released leaves the status at
    // submitted, so the server has the last word (it refuses without saying
    // why)
    const canRevise = application.status === 'submitted' && isApplicant;
    const canReopen =
        application.status === 'withdrawn' &&
        !!application.submittedAt &&
        isApplicant;

    // A reviewer reads the request; its faculty member mostly needs what
    // happened to it, so their copy of the request starts folded
    const reviewerView = isCoordinator && !isApplicant;
    const showRequest = requestOpen ?? reviewerView;
    const sectionNumber =
        application.courseConfirmation?.sectionNumber ||
        application.course?.section ||
        '';
    const pageTitle =
        [
            [courseLabel, sectionNumber && `section ${sectionNumber}`]
                .filter(Boolean)
                .join(', '),
            application.course?.title,
        ]
            .filter(Boolean)
            .join(': ') || 'Course support request';
    const crnOnFile =
        application.courseConfirmation?.crn || application.course?.crn;
    const ranksLA = !!application.programPreferences?.some(
        (p) => p.programType === 'la'
    );
    const laRequested = ranksLA ? requestedLACount(application) : null;
    const applicantName =
        application.applicant?.name || application.applicant?.email;
    const headerFacts = [
        reviewerView && applicantName ? `From ${applicantName}` : null,
        crnOnFile ? `CRN ${crnOnFile}` : null,
        laRequested
            ? `${laRequested} ${laRequested === 1 ? 'LA' : 'LAs'} requested`
            : null,
        application.submittedAt
            ? `Submitted ${formatAnswerDay(application.submittedAt)}`
            : `Updated ${formatAnswerDay(application.updatedAt)}`,
    ];

    const showDraftActions =
        application.status === 'draft' &&
        !roles.includes('admin') &&
        !isCoordinator;
    let primaryAction = null;
    if (!editMode && showDraftActions) {
        primaryAction = (
            <Button
                as={Link}
                to={resumeDraftPath(application._id)}
                variant="primary"
            >
                Continue application
            </Button>
        );
    } else if (!editMode && canResubmit) {
        primaryAction = (
            <Button variant="primary" onClick={() => setEditMode(true)}>
                Edit and resubmit
            </Button>
        );
    } else if (!editMode && canReopen) {
        primaryAction = (
            <StartRevisionButton
                mode="reopen"
                variant="primary"
                applicationId={application._id}
                courseLabel={courseLabel}
                onStarted={openEditorAfterStart}
                onRefused={reloadApplication}
            />
        );
    }
    // A coordinator's Edit request goes in this slot: in the header beside
    // the decision panel, not as another button in the panel
    // Not on the coordinator's own request, which they change as its
    // faculty member
    const asCoordinatorEditor = coordinatorReviewEnabled && !isApplicant;
    const coordinatorEditAction =
        asCoordinatorEditor &&
        coordinatorEditablePrograms(application, roles).length > 0 ? (
            <CoordinatorEditRequest
                key="coordinator-edit"
                application={application}
                roles={roles}
                onSaved={handleCoordinatorEdit}
            />
        ) : null;
    // The submitter changes the instructor at any status once it is filed (a
    // draft has the form); a coordinator of a ranked program, where editing
    // is open to them
    const mayChangeInstructor =
        application.status !== 'draft' &&
        (isApplicant ||
            (asCoordinatorEditor &&
                coordinatorEditablePrograms(application, roles).length > 0));
    const secondaryActions = editMode
        ? []
        : [
              showDraftActions && (
                  <DeleteDraftButton
                      key="delete-draft"
                      applicationId={application._id}
                      courseLabel={courseLabel}
                      onDeleted={() =>
                          navigate('/applications', {
                              state: { success: 'Draft deleted.' },
                          })
                      }
                  />
              ),
              canRevise && (
                  <StartRevisionButton
                      key="revise"
                      mode="revise"
                      variant="outline-primary"
                      applicationId={application._id}
                      courseLabel={courseLabel}
                      onStarted={openEditorAfterStart}
                      onRefused={reloadApplication}
                  />
              ),
              // The submitter keeps the meetings current at any status
              isApplicant && !isCoordinator && (
                  <UpdateMeetingsButton
                      key="update-meetings"
                      application={application}
                      onSaved={handleMeetingsUpdated}
                  />
              ),
              // Withdraw is offered until a program decides
              canWithdraw && (
                  <Dropdown key="more" align="end">
                      <Dropdown.Toggle
                          variant="outline-primary"
                          id="adp-more-actions"
                          aria-label="More actions"
                      >
                          More
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                          <WithdrawApplicationButton
                              asMenuItem
                              applicationId={application._id}
                              courseLabel={courseLabel}
                              status={application.status}
                              onRefused={reloadApplication}
                              onWithdrawn={() =>
                                  navigate('/applications', {
                                      state: {
                                          success:
                                              'Support request withdrawn. You can find it on the Past tab.',
                                          tab: 'past',
                                      },
                                  })
                              }
                          />
                      </Dropdown.Menu>
                  </Dropdown>
              ),
              coordinatorEditAction,
          ];

    // What the program said, first: a reviewer already knows their own
    // program's decisions, so they see only a returned request's notes
    const programMessages = editMode
        ? []
        : programMessagesOf(application).filter(
              (message) => !reviewerView || message.key === 'revision'
          );

    // The decision column: the panel to decide in, or the held decision
    // with Change decision, and what went wrong with either
    const heldCardShown =
        ranksReviewProgram &&
        ownDecisionHeld &&
        !ownDecisionWaitlisted &&
        !changingDecision;
    const panelShown = ranksReviewProgram && canDecide;
    const loadFailedShown = needsOwnDecision && ownDecisionLoadFailed;
    const showDecisionColumn =
        heldCardShown || panelShown || loadFailedShown || !!refusedDecision;
    const nomineeNames =
        coordinatorProgram === 'la'
            ? offerNominees.map((n) => n.name)
            : (application.namedStudents || [])
                  .filter((s) => s.forProgram === coordinatorProgram)
                  .map((s) => s.name || s.email)
                  .filter(Boolean);
    const nomineeText =
        nomineeNames.length > 0
            ? nomineeNames.join(', ')
            : application.programSpecificResponses?.[coordinatorProgram]
                    ?.nomineeStatus === 'needs_help'
              ? 'None named: asks for help finding one'
              : 'None named';
    const programCount = application.programPreferences?.length || 0;
    // Who the decision email goes to: the applicant and, when the request was
    // filed for someone else, its instructor of record
    // (notifications/recipients.js facultyRecipients)
    const onBehalfInstructor = application.isSubmittedOnBehalf
        ? requestInstructorName(application)
        : null;
    const decisionRecipients = [
        applicantName,
        onBehalfInstructor && onBehalfInstructor !== applicantName
            ? `${onBehalfInstructor} (instructor of record)`
            : null,
    ]
        .filter(Boolean)
        .join(' and ');

    const requestSection = (
        <Card
            as="section"
            aria-labelledby="adp-request-heading"
            className="border-0 shadow-sm mb-4"
        >
            <Card.Body>
                <h2 id="adp-request-heading" className="h5 mb-0">
                    <Button
                        variant="link"
                        className="adp-request-toggle"
                        aria-expanded={showRequest}
                        aria-controls="adp-request-body"
                        onClick={() => setRequestOpen(!showRequest)}
                    >
                        <span aria-hidden="true" className="me-2">
                            {showRequest ? '▾' : '▸'}
                        </span>
                        {reviewerView ? 'The request' : 'Your request'}
                    </Button>
                </h2>
                <Collapse in={showRequest}>
                    <div id="adp-request-body">
                        <div className="pt-3">
                            <ApplicationSummary
                                application={application}
                                idPrefix="adp"
                                showConfirmation
                                namedStudentsFiltered={
                                    isCoordinator &&
                                    !roles.includes('admin') &&
                                    !isApplicant
                                }
                            />
                        </div>
                    </div>
                </Collapse>
            </Card.Body>
        </Card>
    );

    const renderRequestBody = () => (
        <>
            {meetingsNote && (
                <Alert
                    ref={meetingsNoteRef}
                    tabIndex={-1}
                    variant="success"
                    dismissible
                    onClose={() => setMeetingsNote(null)}
                >
                    {meetingsNote}
                </Alert>
            )}
            {coordinatorEdited && (
                <Alert
                    ref={coordinatorEditedRef}
                    tabIndex={-1}
                    variant={
                        coordinatorEdited.changed.length > 0
                            ? 'success'
                            : 'info'
                    }
                    dismissible
                    onClose={() => setCoordinatorEdited(null)}
                >
                    {coordinatorEditedNote(coordinatorEdited, facultyEmailed)}
                </Alert>
            )}
            {reviewerView && requestSection}
            {hiringEnabled && hasStandingAward(application, 'la') && (
                // Loads again when an edit changes the award's count
                <StaffingPanel
                    key={`staffing-${laAwardOf(application)?.awarded}`}
                    applicationId={application._id}
                    program="la"
                    isFaculty={isRequestFaculty}
                />
            )}
            {mayChangeInstructor && (
                <InstructorPanel
                    application={application}
                    viewer={userProfile}
                    onSaved={setApplication}
                />
            )}
            <NomineesPanel
                // Loads the nominations again when the award's count changes
                key={`nominees-${laAwardOf(application)?.awarded}`}
                application={application}
                onSaved={setApplication}
                coordinatorReview={coordinatorReviewEnabled}
            />
            {!reviewerView && requestSection}
            {/* Faculty read the program's message at the top of the page */}
            <DecisionHistory
                decisions={application.decisions}
                showMessages={reviewerView}
            />
            <RequestHistory history={application.history} />
        </>
    );

    const budgetText = (() => {
        if (budget === undefined) return 'Loading…';
        if (budget.state === 'none') return 'No budget set for this term';
        if (budget.state === 'error') return "Couldn't load the budget";
        const allocated = budget.allocatedPositions;
        if (!(allocated > 0)) return 'No places funded yet';
        if (budget.remainingPositions < 0) {
            return `${-budget.remainingPositions} over: ${allocated} places funded`;
        }
        return `${budget.remainingPositions} of ${allocated} places left`;
    })();

    // What the coordinator reads before deciding: on a phone it comes
    // first, then the award and the decision
    const renderRequestSummary = () => (
        <Card
            as="section"
            aria-labelledby="adp-summary-heading"
            className="shadow-sm mb-3"
        >
            <Card.Body>
                <h2 id="adp-summary-heading" className="h5">
                    Request summary
                </h2>
                <dl className="adp-decision-summary mb-0">
                    <dt>Applicant</dt>
                    <dd>{applicantName || 'Not provided'}</dd>
                    {programCount > 0 && ownRank && (
                        <>
                            <dt>Ranked</dt>
                            <dd>
                                {programCount === 1
                                    ? `${decisionProgram?.label} only`
                                    : `Choice ${ownRank} of ${programCount}`}
                            </dd>
                        </>
                    )}
                    {coordinatorProgram === 'la' && (
                        <>
                            <dt>Requested</dt>
                            <dd>
                                {requestedAwardCount}{' '}
                                {requestedAwardCount === 1 ? 'LA' : 'LAs'}
                            </dd>
                        </>
                    )}
                    <dt>Nominee</dt>
                    <dd>{nomineeText}</dd>
                    <dt>Budget</dt>
                    <dd>{budgetText}</dd>
                </dl>
            </Card.Body>
        </Card>
    );

    const renderDecisionPanel = () => (
        <Card
            as="section"
            aria-labelledby="adp-decision-heading"
            className="shadow-sm mb-4"
        >
            <Card.Body>
                {/* Focus lands here when a change opens the panel */}
                <h2
                    id="adp-decision-heading"
                    className="h5"
                    ref={changingDecision ? changeCardRef : undefined}
                    tabIndex={changingDecision ? -1 : undefined}
                >
                    {changingDecision
                        ? 'Change your decision'
                        : 'Your decision'}
                </h2>
                {changingDecision && (
                    <Alert variant="info">
                        <p className="mb-2">
                            Your program&apos;s decision now:{' '}
                            <strong>{ownDecisionLabel(ownDecision)}</strong>
                        </p>
                        <p className="mb-0">{heldNote}</p>
                    </Alert>
                )}
                {!changingDecision && editingHeld && <p>{heldNote}</p>}
                {!changingDecision && ownDecisionWaitlisted && (
                    <Alert variant="info">
                        {waitlistOvertaken
                            ? 'Your program waitlisted this application, and another program has since awarded support. You can still decline it.'
                            : 'Your program waitlisted this application. You can still approve or decline it.'}
                        {ownDecision.notes && (
                            <p className="mb-0 mt-2">
                                Waitlist notes: {ownDecision.notes}
                            </p>
                        )}
                    </Alert>
                )}
                {!waitlistOvertaken && requestedAwardCount > 1 && (
                    <div className="mb-3">
                        <Form.Label
                            htmlFor="adp-award-count"
                            className="h6 d-block"
                        >
                            Award
                        </Form.Label>
                        <Form.Select
                            id="adp-award-count"
                            aria-describedby="adp-award-count-help"
                            className="w-auto"
                            value={awardedCount}
                            onChange={(e) =>
                                setAwardChoice({
                                    applicationId: id,
                                    count: Number(e.target.value),
                                })
                            }
                        >
                            {Array.from(
                                { length: requestedAwardCount },
                                (_, i) => i + 1
                            ).map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text
                            id="adp-award-count-help"
                            as="p"
                            className="mb-0"
                        >
                            Requested: {requestedAwardCount}. Approve awards the
                            number chosen; fewer than requested is recorded as a
                            partial award.
                        </Form.Text>
                    </div>
                )}
                {!waitlistOvertaken && (
                    <NomineeOfferPicker
                        nominees={offerNominees}
                        limit={offerLimit}
                        offered={offered}
                        showAnswerBy={hiringEnabled}
                        releaseDate={
                            applicationTerm?.programWindows?.la
                                ?.decisionsReleased
                        }
                        answerBy={nomineeDay}
                        onAnswerByChange={(day) =>
                            setNomineeAnswerBy({
                                applicationId: id,
                                day,
                            })
                        }
                        onChange={(emails) =>
                            setOfferChoice({
                                applicationId: id,
                                emails,
                            })
                        }
                    />
                )}
                <div className="mb-3">
                    {/*
                          The heading is the field's visible
                          name, so it is referenced rather than
                          duplicated into a label: one string to
                          keep correct, and the announced name
                          always matches what is on screen. A
                          placeholder is not a name; it
                          disappears as soon as anyone types.
                        */}
                    <h3 id="adp-decision-notes-label" className="h6">
                        Internal notes (coordinators only)
                    </h3>
                    <Form.Control
                        as="textarea"
                        id="adp-decision-notes"
                        aria-labelledby="adp-decision-notes-label"
                        aria-describedby="adp-decision-notes-help"
                        rows={3}
                        placeholder="Add optional notes about this decision..."
                        value={decisionNotes}
                        onChange={(e) => setDecisionNotes(e.target.value)}
                    />
                    <Form.Text id="adp-decision-notes-help" muted>
                        Coordinators and admins see these notes; faculty do not.
                        If you return the request for revision, they are sent to
                        the faculty member as the revision notes. The message
                        faculty get with a decision is written in the next step.
                    </Form.Text>
                </div>
                {awardBlocked && (
                    <Alert variant="warning">
                        {ownDecisionWaitlisted
                            ? "No instructor of record is named yet. LA can't award support until one is — request a revision so the applicant can add it."
                            : "No instructor of record is named yet. LA can't award or waitlist until one is — request a revision so the applicant can add it."}
                    </Alert>
                )}
                {decisionError && (
                    <Alert variant="danger" className="mt-2">
                        {decisionError}
                    </Alert>
                )}
                <ActionBar
                    className="mt-3"
                    label="Decision"
                    stickyPrimaryOnPhone
                    primary={
                        !waitlistOvertaken && (
                            <Button
                                variant="primary"
                                onClick={() => confirmDecision('approved')}
                                disabled={awardBlocked}
                            >
                                Review approval
                            </Button>
                        )
                    }
                    secondary={[
                        !ownDecisionWaitlisted && !waitlistOvertaken && (
                            <Button
                                key="waitlist"
                                variant="outline-primary"
                                onClick={() => confirmDecision('waitlisted')}
                                disabled={awardBlocked}
                            >
                                Waitlist
                            </Button>
                        ),
                        !ownDecisionDenied && (
                            <Button
                                key="decline"
                                variant="outline-danger"
                                onClick={() => confirmDecision('denied')}
                            >
                                Decline
                            </Button>
                        ),
                        editingHeld && (
                            <Button
                                key="withdraw"
                                variant="outline-danger"
                                onClick={() => confirmDecision('withdraw')}
                            >
                                Withdraw decision
                            </Button>
                        ),
                    ]}
                />
                {(canRequestRevision || changingDecision) && (
                    <div className="d-flex flex-wrap column-gap-3 mt-2">
                        {canRequestRevision && (
                            <Button
                                variant="link"
                                className="px-0"
                                onClick={handleRequestRevision}
                            >
                                Return for revision
                            </Button>
                        )}
                        {/* Leaves the held decision as it was */}
                        {changingDecision && (
                            <Button
                                variant="link"
                                className="px-0"
                                onClick={() => setChangingDecision(false)}
                            >
                                Keep the current decision
                            </Button>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    return (
        <Container>
            <PageHeader
                backTo={
                    reviewProgram
                        ? `/programs/${reviewProgram}`
                        : '/applications'
                }
                backLabel={
                    reviewProgram
                        ? `Back to the ${decisionProgram?.label ?? reviewProgram.toUpperCase()} dashboard`
                        : 'Back to your requests'
                }
                title={pageTitle}
                status={requestOutcomeOf(application)}
                facts={headerFacts}
                primaryAction={primaryAction}
                secondaryActions={secondaryActions}
                secondaryBesideTitle={
                    application.status === 'revision_requested'
                }
            >
                {returnedNote && (
                    <p className="mb-3" data-testid="returned-note">
                        {returnedNote}
                    </p>
                )}
                {asCoordinatorEditor &&
                    editingOffForRevision(application, roles) && (
                        <p className="small text-muted mb-3">
                            Editing is off while the faculty member revises this
                            request.
                        </p>
                    )}
                {programMessages.length > 0 && (
                    <div className="d-grid gap-3">
                        {programMessages.map((message) => (
                            <section
                                key={message.key}
                                aria-labelledby={`adp-message-${message.key}`}
                                className={`border-start border-4 ps-3 ${message.key === 'revision' ? 'border-warning' : 'border-primary'}`}
                            >
                                <h2
                                    id={`adp-message-${message.key}`}
                                    className="h6 mb-1"
                                >
                                    {message.heading}
                                </h2>
                                {message.nextStep && (
                                    <p className="mb-1">{message.nextStep}</p>
                                )}
                                {message.text && (
                                    <p className="mb-0 text-break text-pre-line">
                                        {message.text}
                                    </p>
                                )}
                            </section>
                        ))}
                    </div>
                )}
            </PageHeader>
            {editMode ? (
                <>
                    {application.status === 'revision_requested' && (
                        <RevisionNote
                            notes={application.revisionNotes}
                            requestedBy={application.revisionRequestedBy}
                            requestedAt={application.revisionRequestedAt}
                        />
                    )}
                    {reopenOutcome && (
                        <Alert
                            variant={
                                reopenOutcome.crnRestored
                                    ? 'success'
                                    : 'warning'
                            }
                            id="adp-reopen-note"
                            role="note"
                        >
                            {reopenOutcome.crnRestored
                                ? 'Request reopened. Your CRN is back on it.'
                                : reopenOutcome.formerCrn
                                  ? `Request reopened. CRN ${reopenOutcome.formerCrn} is now on another request this term, so the CRN is blank. Enter the right one on Eligibility and Course before you resubmit.`
                                  : 'Request reopened. Enter the CRN on Eligibility and Course before you resubmit.'}
                        </Alert>
                    )}
                    {/* What requestRevision unticks: the confirmation, and
                        the LA requirements when LA is ranked */}
                    {application.status === 'revision_requested' && (
                        <Alert
                            variant="info"
                            id="adp-reacknowledge-note"
                            role="note"
                        >
                            {application.programPreferences?.some(
                                (p) => p.programType === 'la'
                            )
                                ? 'Before you resubmit, tick the LA requirements on Program Details and the confirmation on Review & Submit again.'
                                : 'Before you resubmit, tick the confirmation on Review & Submit again.'}
                        </Alert>
                    )}
                    <WizardStepIndicator
                        ref={stepTopRef}
                        total={sections.length}
                        current={step}
                        labels={sections.map((s) => s.label)}
                    />
                    <div ref={stepContentRef}>{currentSection?.render()}</div>
                    {submitError && (
                        <Alert
                            ref={submitErrorRef}
                            variant="danger"
                            tabIndex={-1}
                        >
                            {submitError}
                        </Alert>
                    )}
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 mb-2">
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={() => {
                                    setStep((prev) => prev - 1);
                                    saveRevision();
                                }}
                                disabled={step === 1}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={saveAndFinishLater}
                            >
                                Save and finish later
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={saveAndClose}
                            >
                                Save and close
                            </Button>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            {/* Always mounted so the live region is
                                already in the accessibility tree before
                                its text changes — a region that only
                                mounts once the text is ready to announce
                                is unreliable across screen readers. Only
                                shows after a blocked attempt
                                (validationAttempted), and names the
                                specific fields still missing instead of a
                                generic "form incomplete" message (ticket
                                #384). */}
                            <small
                                className="text-danger mb-0"
                                aria-live="polite"
                            >
                                {validationAttempted &&
                                    currentErrors.length > 0 &&
                                    `Missing: ${currentErrors.map((e) => e.label).join(', ')}.`}
                            </small>
                            <div className="d-flex gap-2">
                                {step < sections.length ? (
                                    <Button
                                        variant="primary"
                                        onClick={() =>
                                            attemptAdvance(() => {
                                                setStep((prev) => prev + 1);
                                                saveRevision();
                                            })
                                        }
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        variant="success"
                                        onClick={() =>
                                            attemptAdvance(handleSubmit)
                                        }
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            'Submit request'
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mb-5">
                        <SaveProgressStatus
                            saveState={
                                alreadySubmitted
                                    ? { status: 'idle' }
                                    : saveState
                            }
                            onRetry={saveRevision}
                            note={
                                alreadySubmitted
                                    ? ALREADY_SUBMITTED_SAVE_NOTE
                                    : finishLaterNote
                            }
                            idleText="Your changes are saved each time you move to another step."
                        />
                    </div>
                </>
            ) : (
                <>
                    {application.submitBlocker && (
                        <Alert variant="warning">
                            <strong>
                                This draft can&apos;t be submitted as it stands:
                            </strong>{' '}
                            {application.submitBlocker}
                        </Alert>
                    )}
                    {showDecisionColumn ? (
                        <Row className="g-4">
                            {/* First in the page, so on a phone the decision
                                comes before the request; a right-hand column
                                that stays in view from lg up */}
                            <Col lg={4} className="order-lg-last">
                                <div className="adp-decision-column">
                                    {loadFailedShown && (
                                        <Alert variant="warning">
                                            Your program&apos;s decision for
                                            this application couldn&apos;t be
                                            loaded. Reload the page to review
                                            it.
                                        </Alert>
                                    )}
                                    {refusedDecision && (
                                        <Alert
                                            variant="danger"
                                            ref={refusedDecisionRef}
                                            tabIndex={-1}
                                        >
                                            {refusedDecision}
                                        </Alert>
                                    )}
                                    {heldCardShown && (
                                        <Card
                                            as="section"
                                            aria-labelledby="adp-held-decision-heading"
                                            className="shadow-sm mb-4"
                                        >
                                            <Card.Body>
                                                <h2
                                                    id="adp-held-decision-heading"
                                                    className="h5"
                                                >
                                                    Your program&apos;s decision
                                                </h2>
                                                <p className="fw-bold mb-2">
                                                    {ownDecisionLabel(
                                                        ownDecision
                                                    )}
                                                </p>
                                                {ownDecision.finalStatus ===
                                                'voided' ? (
                                                    <p className="mb-0">
                                                        This award was voided
                                                        when another program
                                                        awarded support, so it
                                                        can&apos;t be changed.
                                                    </p>
                                                ) : (
                                                    <>
                                                        <p>{heldNote}</p>
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={
                                                                startChange
                                                            }
                                                        >
                                                            Change decision
                                                        </Button>
                                                    </>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    )}
                                    {panelShown && renderRequestSummary()}
                                    {panelShown && renderDecisionPanel()}
                                </div>
                            </Col>
                            <Col lg={8}>{renderRequestBody()}</Col>
                        </Row>
                    ) : (
                        renderRequestBody()
                    )}
                    {/* Outside the card, so a reload can't take it away */}
                    <ConfirmDecisionModal
                        type={confirmingDecision}
                        applicantName={
                            application.applicant?.name ||
                            application.applicant?.email
                        }
                        instructorName={
                            application.isSubmittedOnBehalf
                                ? requestInstructorName(application) || ''
                                : undefined
                        }
                        courseLabel={[courseLabel, application.course?.title]
                            .filter(Boolean)
                            .join(' ')}
                        crn={application.courseConfirmation?.crn}
                        programLabel={
                            decisionProgram &&
                            `${decisionProgram.full} (${decisionProgram.label})`
                        }
                        crnEffect={declineCrnEffect}
                        higherRanked={higherRankedLabels}
                        applicationId={id}
                        programType={coordinatorProgram}
                        awardedCount={
                            awardedCount < requestedAwardCount
                                ? awardedCount
                                : undefined
                        }
                        awardText={
                            coordinatorProgram === 'la'
                                ? `${awardedCount} of ${requestedAwardCount} ${requestedAwardCount === 1 ? 'LA' : 'LAs'}`
                                : null
                        }
                        recipients={decisionRecipients}
                        outcomes={outcomes}
                        revisionNotes={decisionNotes.trim()}
                        waitlisted={ownDecisionWaitlisted}
                        waitlistReleased={ownDecisionReleased}
                        holds={programHolds}
                        releaseDate={releaseDate}
                        changing={editingHeld}
                        currentDecision={
                            ownDecision
                                ? ownDecisionLabel(ownDecision)
                                : undefined
                        }
                        onConfirm={recordDecision}
                        onCancel={() => setConfirmingDecision(null)}
                        onRefused={handleDecisionRefused}
                    />
                </>
            )}
        </Container>
    );
}

export default ApplicationDetailPage;
